import { CSSProperties, useEffect, useRef, useState } from 'react';

type Props = {
  loadingSrc?: string;
  src: string;
  srcSet?: string;
  alt: string;
  height?: number;
  width?: number;
  expandSize?: boolean;
  'data-qa-id'?: string;
  objectFit?: 'cover' | 'contain';
  objectPosition?: CSSProperties['objectPosition'];
  aspectRatio?: { width: number; height: number };
  round?: boolean;
  eagerLoad?: boolean;
};

export function Image({
  src,
  loadingSrc,
  srcSet,
  alt,
  width,
  height,
  expandSize,
  objectFit = 'contain',
  objectPosition,
  aspectRatio,
  round,
  eagerLoad,
  'data-qa-id': dataQaId,
}: Props) {
  const ref = useRef<HTMLImageElement>(null);
  const [loaded, setLoaded] = useState(false);

  const backgroundProps: CSSProperties | undefined =
    loadingSrc && !loaded
      ? {
          backgroundImage: `url('${loadingSrc}')`,
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: objectFit,
        }
      : undefined;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (el.complete) setLoaded(true);
    else el.addEventListener('load', () => setLoaded(true));
  }, []);

  return (
    <img
      ref={ref}
      src={src}
      srcSet={srcSet}
      alt={alt}
      width={expandSize ? '100%' : width}
      height={expandSize ? '100%' : height}
      data-qa-id={dataQaId}
      style={{
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit,
        objectPosition,
        borderRadius: round ? '100%' : undefined,
        aspectRatio: aspectRatio
          ? `${aspectRatio.width} / ${aspectRatio.height}`
          : undefined,
        ...backgroundProps,
      }}
      // Eager is the default browser loading strategy
      loading={eagerLoad ? undefined : 'lazy'}
    />
  );
}
